import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cartApi } from "../../services/cartApi";
import { useCartStore } from "../../store/cartStore";
import { useAuthStore } from "../../store/authStore";

export const CART_QUERY_KEY = ["cart"];

const extractCart = (response) => {
  if (!response) return undefined;

  const data = response.data ?? response;

  if (data.cart) return data.cart;
  if (data.data?.cart) return data.data.cart;
  if (data.results?.cart) return data.results.cart;

  if (Array.isArray(data.items)) return data;
  if (Array.isArray(data.data?.items)) return data.data;

  return data;
};

const extractWarnings = (payload) => {
  if (!payload) return [];

  const data = payload.data ?? payload;

  return (
    data.warnings ??
    data.issues ??
    data.data?.warnings ??
    data.data?.issues ??
    data.cart?.warnings ??
    []
  );
};


const isValidCart = (cart) => {
  return Boolean(cart && Array.isArray(cart.items));
};

export const useCart = ({ enabled = true } = {}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const queryClient = useQueryClient();



  const setCart = useCartStore((state) => state.setCart);
  const clearCartStore = useCartStore((state) => state.clearCart);
  const cartSnapshot = useCartStore((state) => state.cart);
  const updateItemQuantityLocal = useCartStore(
    (state) => state.updateItemQuantityLocal
  );
  const removeItemLocal = useCartStore((state) => state.removeItemLocal);

  const syncCart = (cart) => {
    if (!isValidCart(cart)) return;

    setCart(cart);
    queryClient.setQueryData(CART_QUERY_KEY, cart);
  };

  const fetchLatestCart = async () => {
    const response = await cartApi.getCart();
    const cart = extractCart(response);
    const nextWarnings = extractWarnings(response);

    if (isValidCart(cart)) {
      syncCart(cart);
    }

    return cart;
  };

  const cartQuery = useQuery({
    queryKey: CART_QUERY_KEY,
    queryFn: async () => {
      const response = await cartApi.getCart();
      const cart = extractCart(response);
      const nextWarnings = extractWarnings(response);

      return cart;
    },
    enabled: enabled && isAuthenticated,
    initialData: () => queryClient.getQueryData(CART_QUERY_KEY),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 1,
  });

  useEffect(() => {
    if (isValidCart(cartQuery.data)) {
      setCart(cartQuery.data);
    }
  }, [cartQuery.data, setCart]);

  const updateQuantity = useMutation({
    mutationFn: ({ itemId, quantity }) =>
      cartApi.updateQuantity(itemId, quantity),

    onMutate: async ({ itemId, quantity }) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY });

      const previousCart =
        queryClient.getQueryData(CART_QUERY_KEY) ?? cartSnapshot;

      updateItemQuantityLocal(itemId, quantity);

      const nextCart = useCartStore.getState().cart;

      if (isValidCart(nextCart)) {
        queryClient.setQueryData(CART_QUERY_KEY, nextCart);
      }

      return { previousCart };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousCart) {
        syncCart(context.previousCart);
      }
    },

    onSuccess: async (response) => {
      const cart = extractCart(response);
      const nextWarnings = extractWarnings(response);

      if (isValidCart(cart)) {
        syncCart(cart);
        return;
      }

      await fetchLatestCart();
    },
  });

  const removeItem = useMutation({
    mutationFn: (itemId) => cartApi.removeItem(itemId),

    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY });

      const previousCart =
        queryClient.getQueryData(CART_QUERY_KEY) ?? cartSnapshot;

      removeItemLocal(itemId);

      const nextCart = useCartStore.getState().cart;

      if (isValidCart(nextCart)) {
        queryClient.setQueryData(CART_QUERY_KEY, nextCart);
      }

      return { previousCart };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousCart) {
        syncCart(context.previousCart);
      }
    },

    onSuccess: async (response) => {
      const cart = extractCart(response);
      const nextWarnings = extractWarnings(response);

      if (isValidCart(cart)) {
        syncCart(cart);
        return;
      }

      await fetchLatestCart();
    },
  });

  const addToCart = useMutation({
    mutationFn: ({ productId, quantity }) =>
      cartApi.addToCart(productId, quantity),

    onSuccess: async (response) => {
      const cart = extractCart(response);
      const nextWarnings = extractWarnings(response);

      if (isValidCart(cart)) {
        syncCart(cart);
        return;
      }

      await fetchLatestCart();
    },
  });

  const clearCart = useMutation({
    mutationFn: cartApi.clearCart,

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY });

      const previousCart =
        queryClient.getQueryData(CART_QUERY_KEY) ?? cartSnapshot;

      clearCartStore();
      queryClient.setQueryData(CART_QUERY_KEY, {
        items: [],
        items_count: 0,
        subtotal: 0,
        total_discount: 0,
        final_total: 0,
      });

      return { previousCart };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousCart) {
        syncCart(context.previousCart);
      }
    },

    onSuccess: async (response) => {
      const cart = extractCart(response);

      if (isValidCart(cart)) {
        syncCart(cart);
        return;
      }

      syncCart({
        items: [],
        items_count: 0,
        subtotal: 0,
        total_discount: 0,
        final_total: 0,
      });
    },
  });

  const validateCart = useMutation({
    mutationFn: cartApi.validateCart,

    onSuccess: async (response) => {
      const cart = extractCart(response);
      const nextWarnings = extractWarnings(response);

      if (isValidCart(cart)) {
        syncCart(cart);
        return;
      }

      await fetchLatestCart();
    },
  });

  const resolvedCart = cartSnapshot ?? cartQuery.data;
  const warnings = resolvedCart?.warnings ?? [];

  return {
    ...cartQuery,
    cart: resolvedCart,
    warnings,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    validateCart,
    fetchLatestCart,
  };

};
